import React, { useEffect } from "react";
import ReactTooltip from "react-tooltip";
import isEmpty from "lodash/isEmpty";
import moment from "moment";
import { useDispatch } from "react-redux";

import { removeFormErrors } from "store/form/actions";

import { calculateSupplyAndExhaustFlow, getDefaultVentilatorValues } from "utils/ventilation";
import { getOptions, getBaseUnits, getUnitOptions } from "utils/fields";
import { getValidation, getDecimalPlaces } from "utils/fieldValidation";
import { useStateSelector } from "utils/field";
import { NBCMinVentIndex, OBCMinVentIndex } from "utils/buildingCode";

import Field from "components/Field";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import InputWithUnits from "components/Input/InputWithUnits";
import MultiSelect from "components/Input/MultiSelect";
import Accordion from "components/Accordion";
import Checkbox from "components/Input/Checkbox";
import Select from "components/Input/Select";
import AddSystem from "./AddSystem";
import Ventilator from "./Ventilator";
import Dryer from "./Dryer";
import HRV from "./HRV";
import EmptyState from "components/EmptyState";

import classes from "./style.module.scss";
import sharedClasses from "features/Model/sharedStyles.module.scss";

import NonH2kParam from "assets/images/icons/JSX/NonH2kParam";
import Add from "assets/images/icons/JSX/Add";
import Delete from "assets/images/icons/JSX/Delete";
import ResetIcon from "assets/images/icons/Icon-Reset-blue.svg?react";
import Button from "components/Button";
import Tooltip from "components/Tooltip";

const reqUseValidation = getValidation("ventRequirementsUse");
const reqAchValidation = getValidation("ventRequirementsACH");
const reqSupplyValidation = getValidation("ventRequirementsSupply");
const reqExhaustValidation = getValidation("ventRequirementsExhaust");

const roomsLivingValidation = getValidation("numLivingRooms");
const roomsBathValidation = getValidation("numBathrooms");
const roomsBedValidation = getValidation("numBedrooms");
const roomsUtilityValidation = getValidation("numUtility");
const roomsOtherValidation = getValidation("numOtherRooms");
const roomsVentRateValidation = getValidation("ventRoomsVentilationRate");
const roomsDepressLimitValidation = getValidation("ventDepressurizationLimit");

const airDistTypeValidation = getValidation("ventAirDistributionType");
const airDistFanPowerValidation = getValidation("ventAirDistributionFanPower");
const operationScheduleValidation = getValidation("ventOperationSchedule");
const tempCtrlLowerValidation = getValidation("ventTempControlledLower");
const tempCtrlUpperValidation = getValidation("ventTempControlledUpper");

const getAirFlowLabel = (airFlowMetric) => {
    return `${airFlowMetric.toFixed(1)} L/s (${(2.11888 * airFlowMetric).toFixed(1)} cfm)`;
};

const F280VentilationText = ({
    hasWholeHouseSystem = false,
    pvcFromWholeHouseSystems = false,
    wholeHouseRate = 0,
    bedroomMinVentRate = 0,
}) => {
    if (bedroomMinVentRate == null) {
        return <></>;
    }

    if (!hasWholeHouseSystem) {
        return (
            <p>
                No whole-house system is specified. CSA F280-12 calculations will not consider mechanical ventilation
                heat loss and gain.
            </p>
        );
    }

    if (hasWholeHouseSystem && !pvcFromWholeHouseSystems) {
        return (
            <p>
                A whole-house system is specified. CSA F280-12 calculations will consider the minimum ventilation rate
                reported in the Enclosure tab ({bedroomMinVentRate.toFixed(2)} L/s) for the purposes of calculating
                mechanical ventilation losses and gains. This is the <strong>recommended</strong> approach.
            </p>
        );
    }

    if (hasWholeHouseSystem && pvcFromWholeHouseSystems) {
        return (
            <>
                <p>
                    A whole-house system is specified, and you have opted to override the minimum ventilation rate from
                    your selected building code. CSA F280-12 calculations will consider the specified flow rate (
                    {wholeHouseRate.toFixed(2)} L/s) for mechanical heat loss and gain calculations.
                </p>
                <p style={{ marginTop: "0.5rem" }}>
                    This override is <strong>not recommended</strong>, and should only be used when accompanied by
                    ventilation system design.
                </p>
            </>
        );
    }
};

export default ({ accessor, change, isUpgrade = false, formName, canBeEditedFromParametric = true }) => {
    const dispatch = useDispatch();

    const { id: reqUseId } = useStateSelector(`${accessor}.requirements.use`) || {};
    const achValue = useStateSelector(`${accessor}.requirements.ach`);
    const reqSupplyValue = useStateSelector(`${accessor}.requirements.supply`);
    const reqExhaustValue = useStateSelector(`${accessor}.requirements.exhaust`);
    const { id: depressId, value: depressValue } = useStateSelector(`${accessor}.rooms.depressurizationLimit`) || {};
    const { id: airDistTypeId } = useStateSelector(`${accessor}.wholeHouse.airDistributionType`) || {};
    const { id: airDistFanPowerId, value: airDistFanPowerValue } =
        useStateSelector(`${accessor}.wholeHouse.airDistributionFanPower`) || {};
    const { id: operationScheduleId, value: operationScheduleValue } =
        useStateSelector(`${accessor}.wholeHouse.operationSchedule`) || {};
    const houseVolume = useStateSelector(`model.modelData.dimensions.volume.total`) || 0;

    const reqSupplyUnits = useStateSelector(`${accessor}.requirements.supply_u`);
    const reqExhaustUnits = useStateSelector(`${accessor}.requirements.exhaust_u`);
    const depressUnits = useStateSelector(`${accessor}.rooms.depressurizationLimit_u`);
    const airDistFanPowerUnits = useStateSelector(`${accessor}.wholeHouse.airDistributionFanPower_u`);
    const operationScheduleUnits = useStateSelector(`${accessor}.wholeHouse.operationSchedule_u`);
    const tempCtrlLowerUnits = useStateSelector(`${accessor}.wholeHouse.tempControlLower_u`);
    const tempCtrlUpperUnits = useStateSelector(`${accessor}.wholeHouse.tempControlUpper_u`);

    const wholeHouseHrv = useStateSelector(`${accessor}.wholeHouseSystems.hrv`) || {};
    const wholeHouseVentilators = useStateSelector(`${accessor}.wholeHouseSystems.ventilators`) || {};
    const supplementalHrv = useStateSelector(`${accessor}.supplementalSystems.hrv`) || {};
    const supplementalVentilators = useStateSelector(`${accessor}.supplementalSystems.ventilators`) || {};
    const supplementalDryer = useStateSelector(`${accessor}.supplementalSystems.dryer`) || {};

    const f280RoomDedicatedDucts = useStateSelector(`model.modelData.ventilation.f280RoomDedicatedDucts`);

    const modelUnits = useStateSelector(`model.modelData.uiSettings.primaryUnits`) || {};
    const { id: buildingTypeId = 0 } =
        useStateSelector("model.modelData.specifications.buildingType") || {};
    const numDwellingUnits =
        useStateSelector("model.modelData.specifications.murbSpecs.numDwellingUnits") || 1;
    const numNonResUnits = useStateSelector("model.modelData.specifications.murbSpecs.numNonResUnits") || 0;

    const { enabled: dedicatedDuctEnabled = false, rooms = {} } = f280RoomDedicatedDucts || {};

    let totalWholeHouseSupply = 0;
    totalWholeHouseSupply += wholeHouseHrv.supplyFlowrate || 0;
    totalWholeHouseSupply += Object.values(wholeHouseVentilators).reduce(
        (acc, curr) => acc + (curr.supplyFlowrate || 0),
        0
    );
    let totalSupplementalSupply = 0;
    totalSupplementalSupply += supplementalHrv.supplyFlowrate || 0;
    totalSupplementalSupply += supplementalDryer.supplyFlowrate || 0;
    totalSupplementalSupply += Object.values(supplementalVentilators).reduce(
        (acc, curr) => acc + (curr.supplyFlowrate || 0),
        0
    );

    let totalWholeHouseExhaust = 0;
    totalWholeHouseExhaust += wholeHouseHrv.exhaustFlowrate || 0;
    totalWholeHouseExhaust += Object.values(wholeHouseVentilators).reduce(
        (acc, curr) => acc + (curr.exhaustFlowrate || 0),
        0
    );
    let totalSupplementalExhaust = 0;
    totalSupplementalExhaust += supplementalHrv.exhaustFlowrate || 0;
    totalSupplementalExhaust += supplementalDryer.exhaustFlowrate || 0;
    totalSupplementalExhaust += Object.values(supplementalVentilators).reduce(
        (acc, curr) => acc + (curr.exhaustFlowrate || 0),
        0
    );

    const bedroomsEntered = useStateSelector("model.modelData.ventilation.rooms.bedrooms");
    const buildingCodeEntered = useStateSelector("model.modelData.specifications.buildingCode.type");

    const pvcFromWholeHouseSystems = useStateSelector(`${accessor}.pvcFromWholeHouseSystems`) || false;
    const hasWholeHouseSystem = totalWholeHouseExhaust + totalWholeHouseSupply > 0;

    const bedroomMinVentRate = useStateSelector(`model.modelData.ventilation.requirements.minBedroomVentilation`);

    const atticDuctInsUnits = useStateSelector(`model.modelData.ventilation.f280DuctLoss.atticCrawlspace.effRVal_u`);
    const unconditionedCrawlspaceDuctInsUnits = useStateSelector(
        `model.modelData.ventilation.f280DuctLoss.unconditionedCrawlspace.effRVal_u`
    );
    const unconditionedBasementDuctInsUnits = useStateSelector(
        `model.modelData.ventilation.f280DuctLoss.unconditionedBasement.effRVal_u`
    );

    const wholeHouseRate = Math.max(totalWholeHouseExhaust, totalWholeHouseSupply);

    const resetVentilatorFlow = ({ systemAccessor, system, type, isSupplemental }) => {
        change(systemAccessor, {
            ...system,
            ...getDefaultVentilatorValues({
                type,
                isSupplemental,
                buildingTypeId,
                numDwellingUnits,
                numNonResUnits,
            }),
        });
    };

    const resetAllVentilatorFlows = () => {
        if (!isEmpty(wholeHouseHrv)) {
            resetVentilatorFlow({
                systemAccessor: `${accessor}.wholeHouseSystems.hrv`,
                system: wholeHouseHrv,
                type: "hrv",
                isSupplemental: false,
            });
        }

        Object.entries(wholeHouseVentilators).forEach(([id, system]) => {
            resetVentilatorFlow({
                systemAccessor: `${accessor}.wholeHouseSystems.ventilators.${id}`,
                system,
                type: { 2: "rangeHood", 3: "bathroom", 4: "utility" }[system.type?.id],
                isSupplemental: false,
            });
        });

        if (!isEmpty(supplementalDryer)) {
            resetVentilatorFlow({
                systemAccessor: `${accessor}.supplementalSystems.dryer`,
                system: supplementalDryer,
                type: "dryer",
                isSupplemental: true,
            });
        }

        if (!isEmpty(supplementalHrv)) {
            resetVentilatorFlow({
                systemAccessor: `${accessor}.supplementalSystems.hrv`,
                system: supplementalHrv,
                type: "hrv",
                isSupplemental: true,
            });
        }

        Object.entries(supplementalVentilators).forEach(([id, system]) => {
            resetVentilatorFlow({
                systemAccessor: `${accessor}.supplementalSystems.ventilators.${id}`,
                system,
                type: { 2: "rangeHood", 3: "bathroom", 4: "utility" }[system.type?.id],
                isSupplemental: true,
            });
        });
    };

    const handleBedroomChange = () => {
        if (buildingCodeEntered === "NBC") {
            change("model.modelData.ventilation.requirements.minBedroomVentilation", NBCMinVentIndex[bedroomsEntered]);
        }
        if (buildingCodeEntered === "OBC") {
            change("model.modelData.ventilation.requirements.minBedroomVentilation", OBCMinVentIndex[bedroomsEntered]);
        }

        if (bedroomsEntered > 5) {
            change(
                "model.modelData.ventilation.requirements.minBedroomVentilation",
                buildingCodeEntered === "NBC" ? NBCMinVentIndex[5] : OBCMinVentIndex[5]
            );
        }
    };

    const roomSummary = useStateSelector(`model.modelData.roomSummary`) || {};

    const roomOptions = Object.keys(roomSummary)
        .reduce((acc, roomCat) => {
            return [
                ...acc,
                ...Object.keys(roomSummary[roomCat])
                    .map((roomId) => ({
                        label: `${roomSummary[roomCat][roomId]?.name} (${roomCat})` || `Untitled room (${roomCat})`,
                        value: roomId,
                    }))
                    .filter((el) => !el.value.includes("-default-")),
            ];
        }, [])
        .sort((a, b) => a.label.localeCompare(b.label));

    useEffect(() => {
        if (dedicatedDuctEnabled && isEmpty(rooms)) {
            change("model.modelData.ventilation.f280RoomDedicatedDucts.rooms", {
                [`ductRoom-${moment().format("YYYYMMDDHHmmssSS")}}`]: { roomId: null, flowRate: 0 },
            });
        }
    }, [dedicatedDuctEnabled]);

    useEffect(() => {
        if (!dedicatedDuctEnabled) {
            dispatch(removeFormErrors("model.modelData.ventilation.f280RoomDedicatedDucts.rooms"));
        }
    }, [dedicatedDuctEnabled]);

    const dedicatedDuctRoomKeySig = Object.keys(rooms)
        .sort()
        .join(",");
    useEffect(() => {
        if (!dedicatedDuctEnabled) {
            return;
        }
        ReactTooltip.rebuild();
    }, [dedicatedDuctEnabled, dedicatedDuctRoomKeySig]);

    const handleDeleteRoomDuct = (key) => {
        const ductAccessor = `model.modelData.ventilation.f280RoomDedicatedDucts.rooms.${key}`;

        if (Object.keys(rooms).length === 1) {
            change(ductAccessor, { roomId: null, flowRate: 0 });
        } else {
            const { [key]: roomToDelete, ...rest } = rooms;
            change("model.modelData.ventilation.f280RoomDedicatedDucts.rooms", rest || {});
        }

        dispatch(removeFormErrors(ductAccessor));
    };

    const totalDedicatedDuctSupply =
        Object.values(rooms).reduce((sum, curr) => sum + (curr.roomId ? curr.flowRate || 0 : 0), 0) || 0;

    useEffect(() => {
        if (reqUseId === 1) {
            const { supply, exhaust } = calculateSupplyAndExhaustFlow(houseVolume, achValue);

            change(`${accessor}.requirements.supply`, supply);
            change(`${accessor}.requirements.exhaust`, exhaust);
        }
    }, [reqUseId, houseVolume, achValue, change, accessor]);

    const ventRequirementsWaring = reqExhaustValue > totalWholeHouseExhaust || reqSupplyValue > totalWholeHouseSupply;

    return (
        <div className={!isUpgrade && sharedClasses.section}>
            {!isUpgrade && (
                <>
                    <h4 style={{ marginBottom: "1.25rem" }}>Ventilation Requirements</h4>
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                        <Field
                            component={Select}
                            name={`${accessor}.requirements.use`}
                            options={getOptions({ fieldKey: "ventRequirementsUse" })}
                            label="Type"
                            placeholder="Choose Requirements Type"
                            validate={reqUseValidation}
                            info={
                                'Please note that the "Devices over 75 L/s" field is not stored in the .h2k file, and therefore cannot be modified here.'
                            }
                            disabled={!canBeEditedFromParametric}
                            warning={ventRequirementsWaring ? "ventRequirementsWarning" : null}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.requirements.ach`}
                            label="Air Change Rate"
                            placeholder={0.0}
                            disabled={reqUseId !== 1 || !canBeEditedFromParametric}
                            validate={reqAchValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ventRequirementsACH")}
                            change={change}
                            setValue={reqUseId === 1 ? achValue : 0}
                            units={{
                                base: getBaseUnits("ventRequirementsACH", modelUnits),
                                options: getUnitOptions("airtightness"),
                                selected: "ACH50",
                                unitType: "airtightness",
                                accessor: `${accessor}.requirements.ach_u`,
                            }}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.requirements.supply`}
                            label="Supply Flow"
                            placeholder={0.0}
                            disabled={reqUseId !== 2 || !canBeEditedFromParametric}
                            validate={reqSupplyValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ventRequirementsSupply")}
                            change={change}
                            setValue={
                                reqUseId === 0
                                    ? 50
                                    : reqUseId === 1
                                    ? reqSupplyValue
                                    : reqUseId === 2
                                    ? reqSupplyValue
                                    : 0
                            }
                            units={{
                                base: getBaseUnits("ventRequirementsSupply", modelUnits),
                                options: getUnitOptions("airFlow"),
                                selected: reqSupplyUnits,
                                unitType: "airFlow",
                                accessor: `${accessor}.requirements.supply_u`,
                            }}
                            warning={reqSupplyValue > totalWholeHouseSupply ? "ventRequirementsSupplyWarning" : null}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.requirements.exhaust`}
                            label="Exhaust Flow"
                            placeholder={0.0}
                            disabled={reqUseId !== 2 || !canBeEditedFromParametric}
                            validate={reqExhaustValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ventRequirementsExhaust")}
                            change={change}
                            setValue={
                                reqUseId === 0
                                    ? 50
                                    : reqUseId === 1
                                    ? reqExhaustValue
                                    : reqUseId === 2
                                    ? reqExhaustValue
                                    : 0
                            }
                            units={{
                                base: getBaseUnits("ventRequirementsExhaust", modelUnits),
                                options: getUnitOptions("airFlow"),
                                selected: reqExhaustUnits,
                                unitType: "airFlow",
                                accessor: `${accessor}.requirements.exhaust_u`,
                            }}
                            warning={reqExhaustValue > totalWholeHouseExhaust ? "ventRequirementsExhaustWarning" : null}
                        />
                    </InputRow>
                    <h4 style={{ marginBottom: "1.25rem", marginTop: "1.75rem" }}>Room Inputs</h4>
                    <InputRow gridTemplate="1fr 1fr 1fr 1fr 1fr" style={{ alignItems: "flex-end" }}>
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.rooms.living`}
                            label="Kitchen, Living, Dining Rooms"
                            placeholder={0.0}
                            disabled={false || !canBeEditedFromParametric}
                            validate={roomsLivingValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("numLivingRooms")}
                        />
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.rooms.bedrooms`}
                            label="Bedrooms"
                            placeholder={0.0}
                            disabled={false || !canBeEditedFromParametric}
                            validate={roomsBedValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("numBedrooms")}
                        />
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.rooms.bathrooms`}
                            label="Bathrooms"
                            placeholder={0.0}
                            disabled={false || !canBeEditedFromParametric}
                            validate={roomsBathValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("numBathrooms")}
                        />
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.rooms.utility`}
                            label="Utility Rooms"
                            placeholder={0.0}
                            disabled={false || !canBeEditedFromParametric}
                            validate={roomsUtilityValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("numUtility")}
                        />
                        <Field
                            component={Input}
                            type="number"
                            name={`${accessor}.rooms.otherHabitable`}
                            label="Other Habitable Rooms"
                            placeholder={0.0}
                            disabled={false || !canBeEditedFromParametric}
                            validate={roomsOtherValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("numOtherRooms")}
                        />
                    </InputRow>
                    <InputRow gridTemplate="38.64% 38.64% 18.19%" style={{ alignItems: "flex-end" }}>
                        <Field
                            component={Select}
                            name={`${accessor}.rooms.ventilationRate`}
                            options={getOptions({ fieldKey: "ventRoomsVentilationRate" })}
                            label="Ventilation Rate for Other Basement Areas"
                            placeholder="Choose Ventilation Rate"
                            validate={roomsVentRateValidation}
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={Select}
                            name={`${accessor}.rooms.depressurizationLimit`}
                            options={[
                                ...getOptions({ fieldKey: "ventDepressurizationLimit" }),
                                {
                                    label: "User specified",
                                    value: {
                                        id: 3,
                                        value: depressValue,
                                    },
                                },
                            ]}
                            label="Vented Combustion Appliances Depressurization Limit"
                            placeholder="Choose Depressurization Limit"
                            validate={roomsDepressLimitValidation}
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.rooms.depressurizationLimit.value`}
                            label="Value"
                            placeholder={0.0}
                            disabled={depressId !== 3 || !canBeEditedFromParametric}
                            validate={roomsDepressLimitValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ventDepressurizationLimit")}
                            change={change}
                            setValue={depressValue}
                            units={{
                                base: getBaseUnits("ventDepressurizationLimit", modelUnits),
                                options: getUnitOptions("pressure"),
                                selected: depressUnits,
                                unitType: "pressure",
                                accessor: `${accessor}.rooms.depressurizationLimit_u`,
                            }}
                        />
                    </InputRow>
                </>
            )}
            <h4 style={{ marginBottom: "1.25rem", marginTop: "1.75rem" }}>
                Whole-house Ventilation System Description
            </h4>
            <InputRow gridTemplate={isUpgrade ? "2fr 2fr 1fr" : "1fr 1fr 1fr 1fr"}>
                <Field
                    component={Select}
                    name={`${accessor}.wholeHouse.airDistributionType`}
                    options={getOptions({ fieldKey: "ventAirDistributionType" })}
                    label="Air Distribution/Circulation Type"
                    placeholder="Choose Air Distribution Type"
                    validate={airDistTypeValidation}
                    info={
                        "Use this drop down to specify the ductwork configuration. In the case of a continuously operating fan depending on passive outlets for circulation, select Dedicated low volume ductwork."
                    }
                    disabled={!canBeEditedFromParametric}
                />
                <Field
                    component={Select}
                    name={`${accessor}.wholeHouse.airDistributionFanPower`}
                    options={[
                        {
                            label: "User specified",
                            value: {
                                id: 0,
                                value: airDistFanPowerValue,
                            },
                        },
                        ...getOptions({ fieldKey: "ventAirDistributionFanPower" }),
                    ]}
                    label="Air Distribution Fan Power"
                    placeholder="Choose Input Type"
                    validate={airDistFanPowerValidation}
                    disabled={airDistTypeId !== 2 || !canBeEditedFromParametric}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.wholeHouse.airDistributionFanPower.value`}
                    label="Value"
                    placeholder={0.0}
                    disabled={airDistTypeId !== 2 || airDistFanPowerId !== 0 || !canBeEditedFromParametric}
                    validate={airDistFanPowerValidation}
                    setTouched={true}
                    decimals={getDecimalPlaces("ventAirDistributionFanPower")}
                    change={change}
                    setValue={airDistFanPowerValue}
                    units={{
                        base: getBaseUnits("ventAirDistributionFanPower", modelUnits),
                        options: getUnitOptions("power"),
                        selected: airDistFanPowerUnits,
                        unitType: "power",
                        accessor: `${accessor}.wholeHouse.airDistributionFanPower_u`,
                    }}
                />
            </InputRow>
            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                <Field
                    component={Select}
                    name={`${accessor}.wholeHouse.operationSchedule`}
                    options={[
                        ...getOptions({ fieldKey: "ventOperationSchedule" }),
                        {
                            label: "User specified",
                            value: {
                                id: 5,
                                value: operationScheduleValue,
                            },
                        },
                    ]}
                    label="Operation Schedule"
                    placeholder="Choose Operation Schedule"
                    validate={operationScheduleValidation}
                    info={"Leave at the default value (480 min/day)."}
                    disabled={!canBeEditedFromParametric}
                />
                <Field
                    component={InputWithUnits}
                    type="number"
                    name={`${accessor}.wholeHouse.operationSchedule.value`}
                    label="Daily Operation"
                    placeholder={0.0}
                    disabled={operationScheduleId !== 5 || !canBeEditedFromParametric}
                    validate={operationScheduleValidation}
                    setTouched={true}
                    decimals={getDecimalPlaces("ventOperationSchedule")}
                    change={change}
                    setValue={operationScheduleValue}
                    units={{
                        base: getBaseUnits("ventOperationSchedule", modelUnits),
                        options: getUnitOptions("time"),
                        selected: operationScheduleUnits,
                        unitType: "time",
                        accessor: `${accessor}.wholeHouse.operationSchedule_u`,
                    }}
                />
                {operationScheduleId === 6 && (
                    <>
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.wholeHouse.tempControlLower`}
                            label="Lower Temperature Limit"
                            placeholder={0.0}
                            disabled={operationScheduleId !== 6 || !canBeEditedFromParametric}
                            validate={tempCtrlLowerValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ventTempControlledLower")}
                            change={change}
                            units={{
                                base: getBaseUnits("ventTempControlledLower", modelUnits),
                                options: getUnitOptions("temperature"),
                                selected: tempCtrlLowerUnits,
                                unitType: "temperature",
                                accessor: `${accessor}.wholeHouse.tempControlLower_u`,
                            }}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`${accessor}.wholeHouse.tempControlUpper`}
                            label="Upper Temperature Limit"
                            placeholder={0.0}
                            disabled={operationScheduleId !== 6 || !canBeEditedFromParametric}
                            validate={tempCtrlUpperValidation}
                            setTouched={true}
                            decimals={getDecimalPlaces("ventTempControlledUpper")}
                            change={change}
                            units={{
                                base: getBaseUnits("ventTempControlledUpper", modelUnits),
                                options: getUnitOptions("temperature"),
                                selected: tempCtrlUpperUnits,
                                unitType: "temperature",
                                accessor: `${accessor}.wholeHouse.tempControlUpper_u`,
                            }}
                        />
                    </>
                )}
            </InputRow>
            <Accordion
                className={sharedClasses.accordion}
                heading={
                    <div className={sharedClasses.accordionIconHeading}>
                        Ductwork Specifications <NonH2kParam />
                    </div>
                }
                large
            >
                <div>
                    <div className={classes.f280Text}>
                        <h5>Unconditioned Spaces</h5>
                        <p>
                            Use these fields for CSA F280-12 calculations to indicate if ducts serving the building pass
                            through any of the unconditioned spaces listed below, and indicate duct insulation values
                            where appropriate.
                        </p>
                        <p>
                            <strong>Room-by-room:</strong> Select the room(s) served by the ductwork (floor area
                            ignored).
                        </p>
                        <p>
                            <strong>Whole home:</strong> Specify the total floor area (%) served by the ductwork.
                        </p>
                    </div>

                    <InputRow gridTemplate="1.2fr 0.8fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`model.modelData.ventilation.f280DuctLoss.atticCrawlspace.enabled`}
                            label="Attic or open crawl space"
                            setValue={true}
                            large
                            className={classes.checkbox}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`model.modelData.ventilation.f280DuctLoss.atticCrawlspace.effRVal`}
                            label="Duct insulation"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("doorType")}
                            change={change}
                            units={{
                                base: getBaseUnits("doorType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: atticDuctInsUnits,
                                unitType: "thermalResistance",
                                accessor: `model.modelData.ventilation.f280DuctLoss.atticCrawlspace.effRVal_u`,
                            }}
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={MultiSelect}
                            name={`model.modelData.ventilation.f280DuctLoss.atticCrawlspace.selectedRooms`}
                            label="Select rooms served"
                            options={roomOptions}
                            hasSelectAll
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={"model.modelData.ventilation.f280DuctLoss.atticCrawlspace.areaPerc"}
                            label="Approx. floor area served (whole home)"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("fanPumpLowPower")}
                            change={change}
                            units={{
                                base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                options: ["%"],
                                selected: "%",
                                unitType: "fraction",
                                accessor: "model.modelData.ventilation.f280DuctLoss.atticCrawlspace.areaPerc_u",
                            }}
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1.2fr 0.8fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`model.modelData.ventilation.f280DuctLoss.unconditionedCrawlspace.enabled`}
                            label="Enclosed, unconditioned crawl space"
                            setValue={true}
                            large
                            className={classes.checkbox}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`model.modelData.ventilation.f280DuctLoss.unconditionedCrawlspace.effRVal`}
                            label="Duct insulation"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("doorType")}
                            change={change}
                            units={{
                                base: getBaseUnits("doorType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: unconditionedCrawlspaceDuctInsUnits,
                                unitType: "thermalResistance",
                                accessor: `model.modelData.ventilation.f280DuctLoss.unconditionedCrawlspace.effRVal_u`,
                            }}
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={MultiSelect}
                            name={`model.modelData.ventilation.f280DuctLoss.unconditionedCrawlspace.selectedRooms`}
                            label="Select rooms served"
                            options={roomOptions}
                            hasSelectAll
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={"model.modelData.ventilation.f280DuctLoss.unconditionedCrawlspace.areaPerc"}
                            label="Approx. floor area served (whole home)"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("fanPumpLowPower")}
                            change={change}
                            units={{
                                base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                options: ["%"],
                                selected: "%",
                                unitType: "fraction",
                                accessor: "model.modelData.ventilation.f280DuctLoss.unconditionedCrawlspace.areaPerc_u",
                            }}
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1.2fr 0.8fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`model.modelData.ventilation.f280DuctLoss.unconditionedBasement.enabled`}
                            label="Unconditioned basement"
                            setValue={true}
                            large
                            className={classes.checkbox}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={`model.modelData.ventilation.f280DuctLoss.unconditionedBasement.effRVal`}
                            label="Duct insulation"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("fanPumpLowPower")}
                            change={change}
                            units={{
                                base: getBaseUnits("doorType", modelUnits),
                                options: getUnitOptions("thermalResistance"),
                                selected: unconditionedBasementDuctInsUnits,
                                unitType: "thermalResistance",
                                accessor: `model.modelData.ventilation.f280DuctLoss.unconditionedBasement.effRVal_u`,
                            }}
                            disabled={!canBeEditedFromParametric}
                        />
                        <Field
                            component={MultiSelect}
                            name={`model.modelData.ventilation.f280DuctLoss.unconditionedBasement.selectedRooms`}
                            label="Select rooms served"
                            options={roomOptions}
                            hasSelectAll
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={"model.modelData.ventilation.f280DuctLoss.unconditionedBasement.areaPerc"}
                            label="Approx. floor area served (whole home)"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("fanPumpLowPower")}
                            change={change}
                            units={{
                                base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                options: ["%"],
                                selected: "%",
                                unitType: "fraction",
                                accessor: "model.modelData.ventilation.f280DuctLoss.unconditionedBasement.areaPerc_u",
                            }}
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>
                    <InputRow gridTemplate="1.2fr 0.8fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`model.modelData.ventilation.f280DuctLoss.slabOnGradePerimeterIns.enabled`}
                            label="Slab-on-grade with perimeter insulation"
                            setValue={true}
                            large
                            className={classes.checkbox}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                        <div></div>
                        <Field
                            component={MultiSelect}
                            name={`model.modelData.ventilation.f280DuctLoss.slabOnGradePerimeterIns.selectedRooms`}
                            label="Select rooms served"
                            options={roomOptions}
                            hasSelectAll
                        />
                        <Field
                            component={InputWithUnits}
                            type="number"
                            name={"model.modelData.ventilation.f280DuctLoss.slabOnGradePerimeterIns.areaPerc"}
                            label="Approx. floor area served (whole home)"
                            placeholder={0.0}
                            setTouched={true}
                            decimals={getDecimalPlaces("fanPumpLowPower")}
                            change={change}
                            units={{
                                base: getBaseUnits("generalFuelTaxRebate", modelUnits),
                                options: ["%"],
                                selected: "%",
                                unitType: "fraction",
                                accessor: "model.modelData.ventilation.f280DuctLoss.slabOnGradePerimeterIns.areaPerc_u",
                            }}
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>

                    <div className={classes.f280Text}>
                        <h5>Dedicated Ducts</h5>
                        <p>
                            If direct ducted systems are used for specific rooms, indicate those rooms below along with
                            the supply air flow rate to each room. The heat recovery efficiency of a modelled HRV will
                            be used if present. Otherwise, it will be assumed that fresh air is supplied to all
                            indicated rooms.
                        </p>
                    </div>
                    <InputRow gridTemplate="1fr 1fr 1fr">
                        <Field
                            component={Checkbox}
                            name={`model.modelData.ventilation.f280RoomDedicatedDucts.enabled`}
                            label="Enable dedicated duct inputs"
                            setValue={true}
                            large
                            className={classes.checkbox}
                            type="checkbox"
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>
                    {dedicatedDuctEnabled && (
                        <>
                            <Tooltip id="f280-dedicated-duct-delete" delay />
                            {Object.keys(rooms)
                                .sort()
                                .map((key, ind) => {
                                    return (
                                        <InputRow
                                            gridTemplate="1fr 1fr 1fr 1fr"
                                            key={key}
                                            style={{ alignItems: "centre" }}
                                        >
                                            <Field
                                                component={Select}
                                                name={`model.modelData.ventilation.f280RoomDedicatedDucts.rooms.${key}.roomId`}
                                                label="Select room served"
                                                options={roomOptions}
                                                search
                                            />
                                            <Field
                                                component={InputWithUnits}
                                                type="number"
                                                name={`model.modelData.ventilation.f280RoomDedicatedDucts.rooms.${key}.flowRate`}
                                                label="Supply Flow"
                                                placeholder="Enter flow rate"
                                                disabled={!canBeEditedFromParametric}
                                                validate={reqSupplyValidation}
                                                setTouched={true}
                                                decimals={getDecimalPlaces("ventRequirementsSupply")}
                                                change={change}
                                                units={{
                                                    base: getBaseUnits("ventRequirementsSupply", modelUnits),
                                                    options: getUnitOptions("airFlow"),
                                                    selected: reqSupplyUnits,
                                                    unitType: "airFlow",
                                                    accessor: `model.modelData.ventilation.f280RoomDedicatedDucts.rooms.${key}.flowRate_u`,
                                                }}
                                            />

                                            {
                                                <div
                                                    className={classes.delete}
                                                    onClick={() => handleDeleteRoomDuct(key)}
                                                    type="red"
                                                >
                                                    <span data-tip="Delete" data-for="f280-dedicated-duct-delete">
                                                        <Delete />
                                                    </span>
                                                </div>
                                            }
                                        </InputRow>
                                    );
                                })}
                            <InputRow gridTemplate="1fr 1fr 1fr 1fr">
                                <Button
                                    icon={Add}
                                    onClick={() =>
                                        change(
                                            `model.modelData.ventilation.f280RoomDedicatedDucts.rooms.ductRoom-${moment().format(
                                                "YYYYMMDDHHmmssSS"
                                            )}`,
                                            {
                                                roomId: null,
                                                flowRate: 0,
                                            }
                                        )
                                    }
                                    small
                                    type="hollow"
                                >
                                    Add Room
                                </Button>
                            </InputRow>
                        </>
                    )}
                    {totalDedicatedDuctSupply !== 0 && dedicatedDuctEnabled && (
                        <div className={classes.f280Text}>
                            <p>
                                The total mechanical ventilation flow rate used for F280 calculations will be:
                                <strong> {getAirFlowLabel(totalDedicatedDuctSupply)}</strong>
                            </p>
                            <p>All other flow rate inputs will be ignored for F280 room-by-room calculations.</p>
                        </div>
                    )}
                </div>
            </Accordion>
            <div className={classes.resetFlows}>
                <Button
                    type="hollow"
                    small
                    icon={ResetIcon}
                    onClick={resetAllVentilatorFlows}
                    disabled={
                        !canBeEditedFromParametric ||
                        (isEmpty(wholeHouseHrv) &&
                            isEmpty(wholeHouseVentilators) &&
                            isEmpty(supplementalDryer) &&
                            isEmpty(supplementalHrv) &&
                            isEmpty(supplementalVentilators))
                    }
                >
                    Reset all ventilator flows to default
                </Button>
            </div>
            <div className={classes.title} style={{ marginTop: "1.5rem" }}>
                <div>
                    <h4>Whole-house Systems</h4>
                    <p>
                        <strong>Total Supply</strong>: {getAirFlowLabel(totalWholeHouseSupply)}{" "}
                        <span className={classes.divider}></span> <strong>Total Exhaust</strong>:{" "}
                        {getAirFlowLabel(totalWholeHouseExhaust)}
                    </p>
                </div>
                <AddSystem
                    accessor={accessor}
                    hrvExists={!isEmpty(wholeHouseHrv) || !isEmpty(supplementalHrv)}
                    isSupplemental={false}
                    dryerExists={true}
                    change={change}
                    canBeEditedFromParametric={canBeEditedFromParametric}
                />
            </div>
            {isEmpty(wholeHouseHrv) && isEmpty(wholeHouseVentilators) && (
                <EmptyState title="No whole-house ventilation systems to display" style={{}} />
            )}
            {!isEmpty(wholeHouseHrv) && (
                <HRV
                    accessor={`${accessor}.wholeHouseSystems.hrv`}
                    change={change}
                    isSupplemental={false}
                    formName={formName}
                    canBeEditedFromParametric={canBeEditedFromParametric}
                    onResetFlow={() =>
                        resetVentilatorFlow({
                            systemAccessor: `${accessor}.wholeHouseSystems.hrv`,
                            system: wholeHouseHrv,
                            type: "hrv",
                            isSupplemental: false,
                        })
                    }
                />
            )}
            {!isEmpty(wholeHouseVentilators) &&
                Object.keys(wholeHouseVentilators)
                    .sort((a, b) => {
                        if (a < b) {
                            return -1;
                        }
                        if (a > b) {
                            return 1;
                        }
                        return 0;
                    })
                    .map((id) => (
                        <Ventilator
                            accessor={`${accessor}.wholeHouseSystems.ventilators.${id}`}
                            change={change}
                            key={id}
                            isSupplemental={false}
                            formName={formName}
                            onResetFlow={() =>
                                resetVentilatorFlow({
                                    systemAccessor: `${accessor}.wholeHouseSystems.ventilators.${id}`,
                                    system: wholeHouseVentilators[id],
                                    type: { 2: "rangeHood", 3: "bathroom", 4: "utility" }[
                                        wholeHouseVentilators[id].type?.id
                                    ],
                                    isSupplemental: false,
                                })
                            }
                        />
                    ))}
            <div>
                <InputRow style={{ marginTop: "1rem" }} gridTemplate="1fr 1fr">
                    <Field
                        component={Checkbox}
                        name={`${accessor}.pvcFromWholeHouseSystems`}
                        label="Use whole-house ventilation rate as PVC for CSA F280-12"
                        large
                        className={classes.bottomCheckBox}
                        type="checkbox"
                        nonH2kParam="f280Default"
                        disabled={!canBeEditedFromParametric}
                    />
                </InputRow>
                <div className={classes.f280MechVentText}>
                    <F280VentilationText
                        hasWholeHouseSystem={hasWholeHouseSystem}
                        pvcFromWholeHouseSystems={pvcFromWholeHouseSystems}
                        wholeHouseRate={wholeHouseRate}
                        bedroomMinVentRate={bedroomMinVentRate}
                    />
                </div>
            </div>
            <div className={classes.title} style={{ marginTop: "1.25rem" }}>
                <div>
                    <h4>Supplemental Systems</h4>
                    <p>
                        <strong>Total Supply</strong>: {getAirFlowLabel(totalSupplementalSupply)}{" "}
                        <span className={classes.divider}></span> <strong>Total Exhaust</strong>:{" "}
                        {getAirFlowLabel(totalSupplementalExhaust)}
                    </p>
                </div>
                <AddSystem
                    accessor={accessor}
                    hrvExists={!isEmpty(wholeHouseHrv) || !isEmpty(supplementalHrv)}
                    isSupplemental={true}
                    dryerExists={!isEmpty(supplementalDryer)}
                    change={change}
                    formName={formName}
                    canBeEditedFromParametric={canBeEditedFromParametric}
                />
            </div>
            {!isEmpty(supplementalDryer) && (
                <Dryer
                    accessor={`${accessor}.supplementalSystems.dryer`}
                    change={change}
                    isSupplemental={true}
                    formName={formName}
                    onResetFlow={() =>
                        resetVentilatorFlow({
                            systemAccessor: `${accessor}.supplementalSystems.dryer`,
                            system: supplementalDryer,
                            type: "dryer",
                            isSupplemental: true,
                        })
                    }
                />
            )}
            {!isEmpty(supplementalHrv) && (
                <HRV
                    accessor={`${accessor}.supplementalSystems.hrv`}
                    change={change}
                    isSupplemental={true}
                    formName={formName}
                    onResetFlow={() =>
                        resetVentilatorFlow({
                            systemAccessor: `${accessor}.supplementalSystems.hrv`,
                            system: supplementalHrv,
                            type: "hrv",
                            isSupplemental: true,
                        })
                    }
                />
            )}
            {!isEmpty(supplementalVentilators) &&
                Object.keys(supplementalVentilators)
                    .sort((a, b) => {
                        if (a < b) {
                            return -1;
                        }
                        if (a > b) {
                            return 1;
                        }
                        return 0;
                    })
                    .map((id) => (
                        <Ventilator
                            accessor={`${accessor}.supplementalSystems.ventilators.${id}`}
                            change={change}
                            key={id}
                            isSupplemental={true}
                            formName={formName}
                            onResetFlow={() =>
                                resetVentilatorFlow({
                                    systemAccessor: `${accessor}.supplementalSystems.ventilators.${id}`,
                                    system: supplementalVentilators[id],
                                    type: { 2: "rangeHood", 3: "bathroom", 4: "utility" }[
                                        supplementalVentilators[id].type?.id
                                    ],
                                    isSupplemental: true,
                                })
                            }
                        />
                    ))}
            {isEmpty(supplementalDryer) && isEmpty(supplementalHrv) && isEmpty(supplementalVentilators) && (
                <EmptyState title="No supplemental ventilation systems to display" style={{}} />
            )}
        </div>
    );
};
